import { CSSProperties, FC } from 'react'
import { Avatar, Button, Drawer, Dropdown, MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  GlobalOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuOutlined,
  UserOutlined,
} from '@ant-design/icons'
import DropdownNavigation, { DropdownNavigationItem } from '../ui/DropdownNavigation'

const shellContainerStyle: CSSProperties = {
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '0 24px',
}

const headerShellStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  width: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  zIndex: 1000,
  borderBottom: '1px solid rgba(37, 99, 235, 0.08)',
  boxShadow: '0 1px 8px rgba(0, 0, 0, 0.02)',
  isolation: 'isolate',
}

const headerInnerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
}

const headerNavStyle: CSSProperties = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '32px',
}

const desktopButtonsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginLeft: 'auto',
}

const mobileMenuButtonStyle: CSSProperties = {
  display: 'none',
  marginLeft: 'auto',
  alignItems: 'center',
  gap: '16px',
}

interface AppHeaderProps {
  currentPage: string
  user: { email?: string | null } | null
  pinyinItems: DropdownNavigationItem[]
  chengyuItems: DropdownNavigationItem[]
  characterItems: DropdownNavigationItem[]
  languageMenuItems: MenuProps['items']
  userMenuItems: MenuProps['items']
  mobileMenuVisible: boolean
  languageMenuVisible: boolean
  onMobileMenuVisibleChange: (visible: boolean) => void
  onLanguageMenuVisibleChange: (visible: boolean) => void
  onNavigate: (page: string) => void
  onLogin: () => void
  onLogout: () => void
  onChangePassword: () => void
}

const AppHeader: FC<AppHeaderProps> = ({
  currentPage,
  user,
  pinyinItems,
  chengyuItems,
  characterItems,
  languageMenuItems,
  userMenuItems,
  mobileMenuVisible,
  languageMenuVisible,
  onMobileMenuVisibleChange,
  onLanguageMenuVisibleChange,
  onNavigate,
  onLogin,
  onLogout,
  onChangePassword,
}) => {
  const { t, i18n } = useTranslation(['common', 'navigation'])

  return (
    <header
      className="site-header"
      style={headerShellStyle}
    >
      <div className="shell-container" style={shellContainerStyle}>
        <div className="site-header-inner" style={headerInnerStyle}>
          <a
            href="/"
            className="brand-link"
            onClick={(e) => {
              e.preventDefault()
              onNavigate('home')
            }}
          >
            <div className="brand-mark">
              <img src="/images/logo.jpg" alt="WellChinese Logo" className="brand-logo" />
            </div>
            <span className="brand-text" style={{ color: '#0F0F0D' }}>
              {t('common:brand')}
            </span>
          </a>

          <nav style={headerNavStyle}>
            <DropdownNavigation
              title={t('navigation:nav.pinyinLearning')}
              currentPage={currentPage}
              onTitleClick={() => onNavigate('pinyin-module')}
              items={pinyinItems}
            />

            <DropdownNavigation
              title={t('navigation:nav.characterLearning')}
              currentPage={currentPage}
              onTitleClick={() => onNavigate('character-goal')}
              items={characterItems}
            />

            <a
              href="/vocab-goal"
              className={`nav-link header-nav-link${
                ['vocab-goal', 'vocab-learn', 'vocab-difficult-words', 'vocab-learned-words', 'vocab-mastered-words'].includes(currentPage)
                  ? ' is-active'
                  : ''
              }`}
              style={{ position: 'relative' }}
              onClick={(e) => {
                e.preventDefault()
                onNavigate('vocab-goal')
              }}
            >
              {t('navigation:nav.vocabLearning', '词汇')}
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-32px',
                fontSize: '8px',
                fontWeight: '600',
                color: '#ffffff',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                padding: '1px 4px',
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)',
                whiteSpace: 'nowrap',
                transform: 'scale(0.9)',
              }}>
                HSK3.0
              </span>
            </a>

            <DropdownNavigation
              title={t('navigation:nav.idiomLearning')}
              currentPage={currentPage}
              onTitleClick={() => onNavigate('chengyu-learning')}
              items={chengyuItems}
            />

            <a
              href="/resources-download"
              style={undefined}
              className={`nav-link header-nav-link${currentPage === 'resources-download' ? ' is-active' : ''}`}
              onClick={(e) => {
                e.preventDefault()
                onNavigate('resources-download')
              }}
            >
              {t('navigation:nav.learningResources')}
            </a>
          </nav>

          <div className="desktop-nav-buttons" style={desktopButtonsStyle}>
            <Dropdown menu={{ items: languageMenuItems }} placement="bottomRight" trigger={['hover', 'click']}>
              <Button icon={<GlobalOutlined />} type="text" size="middle" className="header-action-button">
                {i18n.language === 'zh' ? '中文' : 'English'}
              </Button>
            </Dropdown>

            {user ? (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Button type="text" size="middle" icon={<UserOutlined />} className="header-action-button">
                  {i18n.language === 'en' ? 'Account' : '账户中心'}
                </Button>
              </Dropdown>
            ) : (
              <Button type="primary" icon={<UserOutlined />} size="middle" onClick={onLogin} className="header-action-button">
                {i18n.language === 'en' ? 'Login' : '登录'}
              </Button>
            )}
          </div>

          <div className="mobile-menu-btn" style={mobileMenuButtonStyle}>
            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomRight"
              trigger={['click']}
              open={languageMenuVisible}
              onOpenChange={onLanguageMenuVisibleChange}
            >
              <Button icon={<GlobalOutlined />} type="text" size="middle" className="mobile-icon-button" />
            </Dropdown>

            <Button
              icon={<MenuOutlined />}
              type="text"
              size="large"
              onClick={() => onMobileMenuVisibleChange(true)}
              style={{ fontSize: '20px' }}
            />
          </div>

          <Drawer
            title={t('common:brand')}
            placement="right"
            onClose={() => onMobileMenuVisibleChange(false)}
            open={mobileMenuVisible}
            width={280}
            styles={{ body: { padding: 0 } }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {user ? (
                <div className="mobile-account-section is-user">
                  <div className="mobile-account-row">
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span className="mobile-account-email">{user.email}</span>
                  </div>
                  <div className="mobile-account-actions">
                    <Button
                      size="small"
                      block
                      icon={<LockOutlined />}
                      onClick={() => {
                        onMobileMenuVisibleChange(false)
                        onChangePassword()
                      }}
                    >
                      {i18n.language === 'en' ? 'Password' : '密码'}
                    </Button>
                    <Button
                      size="small"
                      block
                      danger
                      icon={<LogoutOutlined />}
                      onClick={() => {
                        onMobileMenuVisibleChange(false)
                        onLogout()
                      }}
                    >
                      {i18n.language === 'en' ? 'Logout' : '退出'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mobile-account-section">
                  <Button
                    type="primary"
                    block
                    icon={<UserOutlined />}
                    onClick={() => {
                      onMobileMenuVisibleChange(false)
                      onLogin()
                    }}
                  >
                    {i18n.language === 'en' ? 'Login / Register' : '登录 / 注册'}
                  </Button>
                </div>
              )}

              <div className="mobile-nav-group">
                <div className="mobile-nav-header">{t('navigation:nav.pinyinLearning')}</div>
                {pinyinItems.map((item) => (
                  <div
                    key={item.key}
                    className="mobile-nav-item"
                    onClick={() => {
                      onMobileMenuVisibleChange(false)
                      item.onClick()
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>

              <div className="mobile-nav-group">
                <div className="mobile-nav-header">{t('navigation:nav.characterLearning')}</div>
                {characterItems.map((item) => (
                  <div
                    key={item.key}
                    className="mobile-nav-item"
                    onClick={() => {
                      onMobileMenuVisibleChange(false)
                      item.onClick()
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>

              <div className="mobile-nav-group">
                <div
                  className="mobile-nav-item"
                  onClick={() => {
                    onMobileMenuVisibleChange(false)
                    onNavigate('vocab-goal')
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: ['vocab-goal', 'vocab-learn', 'vocab-difficult-words', 'vocab-learned-words', 'vocab-mastered-words'].includes(currentPage)
                      ? '#2563eb'
                      : '#374151',
                    fontWeight: ['vocab-goal', 'vocab-learn', 'vocab-difficult-words', 'vocab-learned-words', 'vocab-mastered-words'].includes(currentPage)
                      ? '600'
                      : '400'
                  }}
                >
                  <span>{t('navigation:nav.vocabLearning', '词汇')}</span>
                  <span style={{
                    fontSize: '8px',
                    fontWeight: '600',
                    color: '#ffffff',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    transform: 'scale(0.9)',
                  }}>
                    HSK3.0
                  </span>
                </div>
              </div>

              <div className="mobile-nav-group">
                <div className="mobile-nav-header">{t('navigation:nav.idiomLearning')}</div>
                {chengyuItems.map((item) => (
                  <div
                    key={item.key}
                    className="mobile-nav-item"
                    onClick={() => {
                      onMobileMenuVisibleChange(false)
                      item.onClick()
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>

              <div
                className="mobile-nav-item"
                onClick={() => {
                  onMobileMenuVisibleChange(false)
                  onNavigate('resources-download')
                }}
              >
                {t('navigation:nav.learningResources')}
              </div>
            </div>
          </Drawer>
        </div>
      </div>
    </header>
  )
}

export default AppHeader
